/** PDF E — 300 mm glass side panel + 1000 mm door leaf */
export const VOORDEUR_DOOR_LEAF_MM = 1000;
export const VOORDEUR_SIDE_PANEL_MM = 300;
export const VOORDEUR_MIN_DOOR_LEAF_MM = 700;
export const VOORDEUR_MIN_SINGLE_SIDE_TOTAL_MM = VOORDEUR_SIDE_PANEL_MM + VOORDEUR_MIN_DOOR_LEAF_MM;
export const VOORDEUR_MIN_BOTH_PANELS_TOTAL_MM =
  VOORDEUR_SIDE_PANEL_MM * 2 + VOORDEUR_MIN_DOOR_LEAF_MM;
export const VOORDEUR_TOP_LIGHT_HEIGHT_MM = 300;
export const VOORDEUR_NORMAL_HEIGHT_MM = 2200;
export const VOORDEUR_TOP_LIGHT_TOTAL_HEIGHT_MM = 2500;

/** Shared door layout limits (voordeur + achterdeur). */
export const DOOR_MIN_TOTAL_WIDTH_NO_PANEL = 700;
export const DOOR_NO_PANEL_MAX_WIDTH = 1050;
export const DOOR_MAX_TOTAL_WIDTH = 3000;
export const DOOR_MAX_DOOR_LEAF_WIDTH = 1050;
export const DOOR_MIN_DOOR_HEIGHT_MM = 2100;
export const DOOR_NORMAL_MAX_HEIGHT = 2500;
export const DOOR_MAX_DOOR_HEIGHT = 2500;
export const DOOR_MIN_OVERHEAD_HEIGHT = 300;
export const DOOR_MAX_TOTAL_HEIGHT = 3000;

/** Terrasdeur side-panel overhead height cap (mm). */
export const SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM = 800;

/** Terrasdeur side-panel overhead premium per panel (€). */
export const TERRASDEUR_SIDE_PANEL_OVERHEAD_PREMIUM = 500;

/** Terrasdeur minimum main-door leaf width (mm) — always 1400; side panels add on top. */
export const TERRASDEUR_MIN_DOOR_LEAF_MM = 1400;

/** Terrasdeur maximum main-door leaf width (mm); excess goes to side panels when present. */
export const TERRASDEUR_MAX_DOOR_LEAF_MM = 2100;

/** Terrasdeur maximum overall width (mm) when side panels are present. */
export const TERRASDEUR_MAX_TOTAL_WIDTH = 4500;

/** @deprecated Use TERRASDEUR_MIN_DOOR_LEAF_MM — same value for no-panel layouts. */
export const TERRASDEUR_MIN_NO_PANEL_WIDTH = TERRASDEUR_MIN_DOOR_LEAF_MM;

export const TERRASDEUR_MIN_SINGLE_SIDE_TOTAL_MM =
  TERRASDEUR_MIN_DOOR_LEAF_MM + VOORDEUR_SIDE_PANEL_MM;

export const TERRASDEUR_MIN_BOTH_PANELS_TOTAL_MM =
  TERRASDEUR_MIN_DOOR_LEAF_MM + VOORDEUR_SIDE_PANEL_MM * 2;

export const getDoorMinTotalWidth = (designType) => {
  const dt = designType || 'no-panel';
  if (dt === 'both-panels') return VOORDEUR_MIN_BOTH_PANELS_TOTAL_MM;
  if (dt === 'left-panel' || dt === 'right-panel') return VOORDEUR_MIN_SINGLE_SIDE_TOTAL_MM;
  return DOOR_MIN_TOTAL_WIDTH_NO_PANEL;
};

export const getTerrasdeurMinTotalWidth = (designType) => {
  const dt = designType || 'no-panel';
  if (dt === 'both-panels') return TERRASDEUR_MIN_BOTH_PANELS_TOTAL_MM;
  if (dt === 'left-panel' || dt === 'right-panel') return TERRASDEUR_MIN_SINGLE_SIDE_TOTAL_MM;
  return TERRASDEUR_MIN_DOOR_LEAF_MM;
};

export const getTerrasdeurMaxTotalWidth = (designType) => {
  const dt = designType || 'no-panel';
  if (dt === 'no-panel') return TERRASDEUR_MAX_DOOR_LEAF_MM;
  return TERRASDEUR_MAX_TOTAL_WIDTH;
};

const hasTopLightActive = (cfg) =>
  cfg.doorType === 'top-light' || cfg.topLight === 'with-top-light';

/** Keep the main door leaf within max width by pushing excess into side panel(s). */
export const enforceMaxDoorLeaf = (cfg) => {
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  if (!hasLeft && !hasRight) return cfg;

  const total = Math.round(Number(cfg.width) || 0);
  let left = hasLeft ? Math.round(Number(cfg.leftPanelWidth) || 0) : 0;
  let right = hasRight ? Math.round(Number(cfg.rightPanelWidth) || 0) : 0;
  const door = total - left - right;
  if (door <= DOOR_MAX_DOOR_LEAF_WIDTH) return cfg;

  const excess = door - DOOR_MAX_DOOR_LEAF_WIDTH;
  if (hasLeft && hasRight) {
    const addLeft = Math.ceil(excess / 2);
    left += addLeft;
    right += excess - addLeft;
  } else if (hasLeft) {
    left += excess;
  } else {
    right += excess;
  }

  return {
    ...cfg,
    ...(hasLeft ? { leftPanelWidth: left } : {}),
    ...(hasRight ? { rightPanelWidth: right } : {}),
  };
};

/** Push terrasdeur main-door width above 2100 mm into side panel(s) (same split logic as voordeur @ 1050). */
export const enforceMaxTerrasdeurDoorLeaf = (cfg) => {
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  if (!hasLeft && !hasRight) return cfg;

  const total = Math.round(Number(cfg.width) || 0);
  let left = hasLeft ? Math.round(Number(cfg.leftPanelWidth) || 0) : 0;
  let right = hasRight ? Math.round(Number(cfg.rightPanelWidth) || 0) : 0;
  const door = total - left - right;
  if (door <= TERRASDEUR_MAX_DOOR_LEAF_MM) return cfg;

  const excess = door - TERRASDEUR_MAX_DOOR_LEAF_MM;
  if (hasLeft && hasRight) {
    const addLeft = Math.ceil(excess / 2);
    left += addLeft;
    right += excess - addLeft;
  } else if (hasLeft) {
    left += excess;
  } else {
    right += excess;
  }

  return {
    ...cfg,
    ...(hasLeft ? { leftPanelWidth: left } : {}),
    ...(hasRight ? { rightPanelWidth: right } : {}),
  };
};

/** Enforce side-panel minimums, door-leaf minimum, and layout minimum total width. */
export const enforceDoorWidthBounds = (cfg) => {
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  if (!hasLeft && !hasRight) return cfg;

  let total = Math.round(Number(cfg.width) || 0);
  const minTotal = getDoorMinTotalWidth(designType);
  if (total < minTotal) total = minTotal;

  let left = hasLeft
    ? Math.round(Number(cfg.leftPanelWidth) ?? VOORDEUR_SIDE_PANEL_MM)
    : 0;
  let right = hasRight
    ? Math.round(Number(cfg.rightPanelWidth) ?? VOORDEUR_SIDE_PANEL_MM)
    : 0;

  if (hasLeft) left = Math.max(VOORDEUR_SIDE_PANEL_MM, left);
  if (hasRight) right = Math.max(VOORDEUR_SIDE_PANEL_MM, right);

  let door = total - left - right;

  if (door < VOORDEUR_MIN_DOOR_LEAF_MM) {
    const need = VOORDEUR_MIN_DOOR_LEAF_MM - door;
    if (hasLeft && hasRight) {
      const shrinkLeft = Math.min(left - VOORDEUR_SIDE_PANEL_MM, Math.ceil(need / 2));
      const shrinkRight = Math.min(right - VOORDEUR_SIDE_PANEL_MM, need - shrinkLeft);
      left -= shrinkLeft;
      right -= shrinkRight;
    } else if (hasLeft) {
      left = Math.max(VOORDEUR_SIDE_PANEL_MM, left - need);
    } else {
      right = Math.max(VOORDEUR_SIDE_PANEL_MM, right - need);
    }
  }

  return enforceMaxDoorLeaf({
    ...cfg,
    width: total,
    ...(hasLeft ? { leftPanelWidth: left } : {}),
    ...(hasRight ? { rightPanelWidth: right } : {}),
  });
};

/** Terrasdeur — main door 1400–2100 mm; side panels min 300 mm (voordeur/achterdeur rules). */
export const enforceTerrasdeurWidthBounds = (cfg) => {
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);

  if (!hasLeft && !hasRight) {
    const width = Math.round(Number(cfg.width) || 0);
    const clamped = Math.max(
      TERRASDEUR_MIN_DOOR_LEAF_MM,
      Math.min(TERRASDEUR_MAX_DOOR_LEAF_MM, width)
    );
    return { ...cfg, width: clamped };
  }

  let total = Math.round(Number(cfg.width) || 0);
  const minTotal = getTerrasdeurMinTotalWidth(designType);
  const maxTotal = getTerrasdeurMaxTotalWidth(designType);
  if (total < minTotal) total = minTotal;
  if (total > maxTotal) total = maxTotal;

  let left = hasLeft
    ? Math.round(Number(cfg.leftPanelWidth) ?? VOORDEUR_SIDE_PANEL_MM)
    : 0;
  let right = hasRight
    ? Math.round(Number(cfg.rightPanelWidth) ?? VOORDEUR_SIDE_PANEL_MM)
    : 0;

  if (hasLeft) left = Math.max(VOORDEUR_SIDE_PANEL_MM, left);
  if (hasRight) right = Math.max(VOORDEUR_SIDE_PANEL_MM, right);

  let door = total - left - right;

  if (door < TERRASDEUR_MIN_DOOR_LEAF_MM) {
    const need = TERRASDEUR_MIN_DOOR_LEAF_MM - door;
    if (hasLeft && hasRight) {
      const shrinkLeft = Math.min(left - VOORDEUR_SIDE_PANEL_MM, Math.ceil(need / 2));
      const shrinkRight = Math.min(right - VOORDEUR_SIDE_PANEL_MM, need - shrinkLeft);
      left -= shrinkLeft;
      right -= shrinkRight;
    } else if (hasLeft) {
      left = Math.max(VOORDEUR_SIDE_PANEL_MM, left - need);
    } else {
      right = Math.max(VOORDEUR_SIDE_PANEL_MM, right - need);
    }
    door = total - left - right;
    if (door < TERRASDEUR_MIN_DOOR_LEAF_MM) {
      total += TERRASDEUR_MIN_DOOR_LEAF_MM - door;
      if (total > maxTotal) total = maxTotal;
    }
  }

  return enforceMaxTerrasdeurDoorLeaf({
    ...cfg,
    width: total,
    ...(hasLeft ? { leftPanelWidth: left } : {}),
    ...(hasRight ? { rightPanelWidth: right } : {}),
  });
};

/** After generic design-type width sync, apply terrasdeur minimum totals. */
export const applyTerrasdeurDesignTypeChange = (prev, nextDesignType) => {
  const next = { ...prev, ...applyDesignTypeChange(prev, nextDesignType) };
  const minTotal = getTerrasdeurMinTotalWidth(nextDesignType);
  return {
    ...next,
    width: Math.max(Number(next.width) || 0, minTotal),
  };
};

/** Keep main door section height within min/max when top light is active. */
export const enforceDoorHeightBounds = (cfg) => {
  if (!hasTopLightActive(cfg)) return cfg;
  const total = Math.round(Number(cfg.height) || 0);
  let overhead = Math.round(Number(cfg.overheadHeight) || VOORDEUR_TOP_LIGHT_HEIGHT_MM);

  if (overhead < DOOR_MIN_OVERHEAD_HEIGHT) {
    overhead = DOOR_MIN_OVERHEAD_HEIGHT;
  }

  let door = total - overhead;

  if (door > DOOR_MAX_DOOR_HEIGHT) {
    overhead = total - DOOR_MAX_DOOR_HEIGHT;
  } else if (door < DOOR_MIN_DOOR_HEIGHT_MM) {
    overhead = total - DOOR_MIN_DOOR_HEIGHT_MM;
  }

  return { ...cfg, overheadHeight: overhead };
};

/**
 * Achterdeur two-parts (sandwich): enable parapet on active side panels, sync heights/panel type,
 * and set each side panel width equal to the main door leaf width.
 */
export const applyAchterdeurTwoPartsSandwichSync = (config, overrides = {}) => {
  const cfg = { ...config, ...overrides };
  if (cfg.numberOfParts !== 'two-parts') return cfg;

  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  const parapetHeight = Math.round(Number(cfg.parapetHeight) || 250);
  const panelType = cfg.panelType || 'sandwich-no-mullions';

  const next = {
    ...cfg,
    parapet: 'with-parapet',
    parapetHeight,
    panelType,
  };

  if (hasLeft) {
    next.leftPanelParapet = true;
    next.leftPanelParapetHeight = parapetHeight;
    next.leftPanelParapetPanelType = panelType;
  }
  if (hasRight) {
    next.rightPanelParapet = true;
    next.rightPanelParapetHeight = parapetHeight;
    next.rightPanelParapetPanelType = panelType;
  }

  return enforceDoorWidthBounds(next);
};

export const clearAchterdeurTwoPartsSideParapets = (config) => ({
  ...config,
  parapet: 'no-parapet',
  leftPanelParapet: false,
  rightPanelParapet: false,
});

/**
 * Terrasdeur — when main-door parapet is on, enable parapet on all active side panels
 * with the same height, panel type, and color as the main door.
 */
export const applyTerrasdeurParapetSync = (config, overrides = {}) => {
  const cfg = { ...config, ...overrides };
  if (cfg.parapet !== 'with-parapet') {
    return clearTerrasdeurSideParapets(cfg);
  }

  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);
  const parapetHeight = Math.round(Number(cfg.parapetHeight) || 250);
  const panelType = cfg.panelType || 'sandwich-no-mullions';

  const next = {
    ...cfg,
    parapet: 'with-parapet',
    parapetHeight,
    panelType,
  };

  if (hasLeft) {
    next.leftPanelParapet = true;
    next.leftPanelParapetHeight = parapetHeight;
    next.leftPanelParapetPanelType = panelType;
    if (cfg.parapetColor !== undefined) {
      next.leftPanelParapetColor = cfg.parapetColor;
    }
  } else {
    next.leftPanelParapet = false;
  }

  if (hasRight) {
    next.rightPanelParapet = true;
    next.rightPanelParapetHeight = parapetHeight;
    next.rightPanelParapetPanelType = panelType;
    if (cfg.parapetColor !== undefined) {
      next.rightPanelParapetColor = cfg.parapetColor;
    }
  } else {
    next.rightPanelParapet = false;
  }

  return enforceTerrasdeurWidthBounds(next);
};

export const clearTerrasdeurSideParapets = (config) => ({
  ...config,
  leftPanelParapet: false,
  rightPanelParapet: false,
});

/**
 * Terrasdeur — sync side-panel parapet across active panels when both-panels is selected:
 * one parapet on a single side panel, matching settings on both when both-panels.
 */
export const applyTerrasdeurSidePanelParapetSync = (config, overrides = {}) => {
  const cfg = { ...config, ...overrides };
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);

  const togglingOff =
    overrides.leftPanelParapet === false || overrides.rightPanelParapet === false;
  const togglingOn =
    overrides.leftPanelParapet === true || overrides.rightPanelParapet === true;

  let parapetEnabled;
  if (togglingOff && !togglingOn) {
    parapetEnabled = false;
  } else if (togglingOn) {
    parapetEnabled = true;
  } else {
    parapetEnabled = !!(cfg.leftPanelParapet || cfg.rightPanelParapet);
  }

  const parapetHeight = Math.round(
    Number(
      overrides.leftPanelParapetHeight ??
        overrides.rightPanelParapetHeight ??
        cfg.leftPanelParapetHeight ??
        cfg.rightPanelParapetHeight
    ) || 250
  );
  const panelType =
    overrides.leftPanelParapetPanelType ??
    overrides.rightPanelParapetPanelType ??
    cfg.leftPanelParapetPanelType ??
    cfg.rightPanelParapetPanelType ??
    'sandwich-no-mullions';
  const parapetColor =
    overrides.leftPanelParapetColor ??
    overrides.rightPanelParapetColor ??
    cfg.leftPanelParapetColor ??
    cfg.rightPanelParapetColor;

  const next = { ...cfg };

  if (!parapetEnabled) {
    next.leftPanelParapet = false;
    next.rightPanelParapet = false;
    return next;
  }

  if (hasLeft) {
    next.leftPanelParapet = true;
    next.leftPanelParapetHeight = parapetHeight;
    next.leftPanelParapetPanelType = panelType;
    if (parapetColor !== undefined) {
      next.leftPanelParapetColor = parapetColor;
    }
  } else {
    next.leftPanelParapet = false;
  }

  if (hasRight) {
    next.rightPanelParapet = true;
    next.rightPanelParapetHeight = parapetHeight;
    next.rightPanelParapetPanelType = panelType;
    if (parapetColor !== undefined) {
      next.rightPanelParapetColor = parapetColor;
    }
  } else {
    next.rightPanelParapet = false;
  }

  return next;
};

/**
 * Terrasdeur — sync side-panel overhead across active panels:
 * one overhead on a single side panel, equal overheads on both when both-panels.
 */
export const applyTerrasdeurSidePanelOverheadSync = (config, overrides = {}) => {
  const cfg = { ...config, ...overrides };
  const designType = cfg.designType || 'no-panel';
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);

  const togglingOff =
    overrides.leftPanelOverhead === false || overrides.rightPanelOverhead === false;
  const togglingOn =
    overrides.leftPanelOverhead === true || overrides.rightPanelOverhead === true;

  let overheadEnabled;
  if (togglingOff && !togglingOn) {
    overheadEnabled = false;
  } else if (togglingOn) {
    overheadEnabled = true;
  } else {
    overheadEnabled = !!(cfg.leftPanelOverhead || cfg.rightPanelOverhead);
  }

  const overheadHeight = Math.round(
    Math.min(
      SIDE_PANEL_OVERHEAD_MAX_HEIGHT_MM,
      Number(
        overrides.leftPanelOverheadHeight ??
          overrides.rightPanelOverheadHeight ??
          cfg.leftPanelOverheadHeight ??
          cfg.rightPanelOverheadHeight
      ) || VOORDEUR_TOP_LIGHT_HEIGHT_MM
    )
  );
  const windowType =
    overrides.leftPanelOverheadWindowType ??
    overrides.rightPanelOverheadWindowType ??
    cfg.leftPanelOverheadWindowType ??
    cfg.rightPanelOverheadWindowType ??
    'fixed-sash';

  const next = { ...cfg };

  if (!overheadEnabled) {
    next.leftPanelOverhead = false;
    next.rightPanelOverhead = false;
    return next;
  }

  if (hasLeft) {
    next.leftPanelOverhead = true;
    next.leftPanelOverheadHeight = overheadHeight;
    next.leftPanelOverheadWindowType = windowType;
  } else {
    next.leftPanelOverhead = false;
  }

  if (hasRight) {
    next.rightPanelOverhead = true;
    next.rightPanelOverheadHeight = overheadHeight;
    next.rightPanelOverheadWindowType = windowType;
  } else {
    next.rightPanelOverhead = false;
  }

  return next;
};

export const finalizeTerrasdeurUpdate = (next) =>
  next.parapet === 'with-parapet' ? applyTerrasdeurParapetSync(next) : next;

/** Main door + each active side panel when two-parts (sandwich) is selected. */
export const countAchterdeurTwoPartsParapetSections = (config) => {
  if (config.numberOfParts !== 'two-parts') return 0;
  const designType = config.designType || 'no-panel';
  let count = 1;
  if (hasLeftSidePanel(designType)) count += 1;
  if (hasRightSidePanel(designType)) count += 1;
  return count;
};

/** €50 per sandwich parapet section (achterdeur two-parts, terrasdeur side parapet). */
export const DOOR_SANDWICH_SECTION_PREMIUM = 50;

/** Terrasdeur main door with parapet = two sections (upper glass + lower panel) at €50 each. */
export const TERRASDEUR_MAIN_PARAPET_SECTIONS = 2;

const isSandwichParapetPanel = (panelType) =>
  Boolean(panelType && panelType !== 'glass');

/**
 * Terrasdeur sandwich parapet section count for display (main door counts as 2 when parapet is on).
 */
export const countTerrasdeurSandwichParapetSections = (config) => {
  const designType = config.designType || 'no-panel';
  let count = 0;

  if (config.parapet === 'with-parapet') {
    count += TERRASDEUR_MAIN_PARAPET_SECTIONS;
  }

  if (
    hasLeftSidePanel(designType) &&
    config.leftPanelParapet &&
    isSandwichParapetPanel(config.leftPanelParapetPanelType)
  ) {
    count += 1;
  }

  if (
    hasRightSidePanel(designType) &&
    config.rightPanelParapet &&
    isSandwichParapetPanel(config.rightPanelParapetPanelType)
  ) {
    count += 1;
  }

  return count;
};

/** Terrasdeur sandwich parapet premium — main door €100 when parapet on; side panels €50 each. */
export const calcTerrasdeurSandwichParapetPremium = (config) =>
  countTerrasdeurSandwichParapetSections(config) * DOOR_SANDWICH_SECTION_PREMIUM;

/** Active terrasdeur side panels with overhead enabled. */
export const countTerrasdeurSidePanelOverheads = (config) => {
  const designType = config.designType || 'no-panel';
  let count = 0;
  if (hasLeftSidePanel(designType) && config.leftPanelOverhead) count += 1;
  if (hasRightSidePanel(designType) && config.rightPanelOverhead) count += 1;
  return count;
};

/** Terrasdeur side-panel overhead premium — €500 per panel with overhead. */
export const calcTerrasdeurSidePanelOverheadPremium = (config) =>
  countTerrasdeurSidePanelOverheads(config) * TERRASDEUR_SIDE_PANEL_OVERHEAD_PREMIUM;

export const hasLeftSidePanel = (designType) =>
  designType === 'left-panel' || designType === 'both-panels';

export const hasRightSidePanel = (designType) =>
  designType === 'right-panel' || designType === 'both-panels';

export const resolveSidePanelWidth = (config, side) => {
  const designType = config.designType || 'no-panel';
  const totalWidth = Number(config.width) || 0;
  const hasLeft = hasLeftSidePanel(designType);
  const hasRight = hasRightSidePanel(designType);

  if (side === 'left' && hasLeft) {
    return config.leftPanelWidth !== undefined
      ? config.leftPanelWidth
      : designType === 'both-panels'
        ? Math.round((totalWidth - VOORDEUR_DOOR_LEAF_MM) / 2)
        : Math.max(0, totalWidth - VOORDEUR_DOOR_LEAF_MM);
  }
  if (side === 'right' && hasRight) {
    return config.rightPanelWidth !== undefined
      ? config.rightPanelWidth
      : designType === 'both-panels'
        ? Math.round((totalWidth - VOORDEUR_DOOR_LEAF_MM) / 2)
        : Math.max(0, totalWidth - VOORDEUR_DOOR_LEAF_MM);
  }
  return 0;
};

/** Mirror Voordeur.jsx designType change — width and panel fields stay in sync for pricing. */
export const applyDesignTypeChange = (prev, nextDesignType) => {
  const prevType = prev.designType || 'no-panel';
  const hadLeft = hasLeftSidePanel(prevType);
  const hadRight = hasRightSidePanel(prevType);
  const hasLeft = hasLeftSidePanel(nextDesignType);
  const hasRight = hasRightSidePanel(nextDesignType);

  let width = Number(prev.width) || 0;
  let leftPanelWidth = prev.leftPanelWidth;
  let rightPanelWidth = prev.rightPanelWidth;

  if (hadLeft && !hasLeft) {
    width -= resolveSidePanelWidth(prev, 'left');
    leftPanelWidth = undefined;
  }
  if (hadRight && !hasRight) {
    width -= resolveSidePanelWidth(prev, 'right');
    rightPanelWidth = undefined;
  }

  if (!hadLeft && hasLeft) {
    width += VOORDEUR_SIDE_PANEL_MM;
    leftPanelWidth = VOORDEUR_SIDE_PANEL_MM;
  }
  if (!hadRight && hasRight) {
    width += VOORDEUR_SIDE_PANEL_MM;
    rightPanelWidth = VOORDEUR_SIDE_PANEL_MM;
  }

  if (hasLeft && hasRight) {
    const minTotal = VOORDEUR_MIN_BOTH_PANELS_TOTAL_MM;
    if (width < minTotal) {
      width = minTotal;
    }
    leftPanelWidth = leftPanelWidth ?? VOORDEUR_SIDE_PANEL_MM;
    rightPanelWidth = rightPanelWidth ?? VOORDEUR_SIDE_PANEL_MM;
  }

  const minWidth =
    hasLeft && hasRight
      ? VOORDEUR_MIN_BOTH_PANELS_TOTAL_MM
      : hasLeft || hasRight
        ? VOORDEUR_MIN_SINGLE_SIDE_TOTAL_MM
        : DOOR_MIN_TOTAL_WIDTH_NO_PANEL;

  return {
    designType: nextDesignType,
    width: Math.max(width, minWidth),
    leftPanelWidth,
    rightPanelWidth,
  };
};

/** Mirror Voordeur.jsx doorType change — height fields stay in sync for pricing. */
export const applyDoorTypeChange = (prev, nextDoorType) => {
  const wasTopLight = prev.doorType === 'top-light';
  const isTopLight = nextDoorType === 'top-light';
  if (!wasTopLight && isTopLight) {
    return {
      doorType: nextDoorType,
      height: VOORDEUR_TOP_LIGHT_TOTAL_HEIGHT_MM,
      overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
    };
  }
  if (wasTopLight && !isTopLight) {
    return {
      doorType: nextDoorType,
      height: VOORDEUR_NORMAL_HEIGHT_MM,
    };
  }
  return { doorType: nextDoorType };
};

/** Mirror Achterdeur.jsx topLight change — height fields stay in sync with voordeur rules. */
export const applyTopLightChange = (prev, nextTopLight) => {
  const wasTopLight = prev.topLight === 'with-top-light';
  const isTopLight = nextTopLight === 'with-top-light';
  if (!wasTopLight && isTopLight) {
    return {
      topLight: nextTopLight,
      height: VOORDEUR_TOP_LIGHT_TOTAL_HEIGHT_MM,
      overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
    };
  }
  if (wasTopLight && !isTopLight) {
    return {
      topLight: nextTopLight,
      height: VOORDEUR_NORMAL_HEIGHT_MM,
    };
  }
  return { topLight: nextTopLight };
};

/** Apply a single option change the same way Voordeur.jsx does before pricing. */
export const applyVoordeurOptionChange = (config, field, value) => {
  if (field === 'designType') {
    return { ...config, ...applyDesignTypeChange(config, value) };
  }
  if (field === 'doorType') {
    return { ...config, ...applyDoorTypeChange(config, value) };
  }
  if (field === 'doorStyle') {
    const next = { ...config, doorStyle: value };
    if (value === 'style-6') {
      next.style6Layout = Number(config.numberOfParts) === 4 ? 'k' : 'm';
    }
    return next;
  }
  if (field === 'numberOfParts') {
    const parts = Number(value);
    if (config.doorStyle === 'style-6') {
      return {
        ...config,
        numberOfParts: parts,
        style6Layout: parts === 4 ? 'k' : 'm',
      };
    }
    return { ...config, numberOfParts: parts };
  }
  return { ...config, [field]: value };
};
